import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import React, { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { atomFamily, useRecoilState, useRecoilValue } from 'recoil';

import * as api from '@gitbook/api-client';
import { createSiteEvent, logEvent } from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import type { IconDefinition, Key } from '@gitbook/spine-react';
import {
    AtomButton,
    BadgeCompletion,
    Button,
    Card,
    CircularProgress,
    Dialog,
    DialogRoot,
    DialogTrigger,
    Disclosure,
    DisclosureChevron,
    DisclosureGroup,
    DisclosurePanel,
    DisclosureTrigger,
    Heading,
    LoadingPanel,
    renderIcon,
    toast,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assert, assertNever } from '@gitbook/util-ts';

import { SiteOverviewSectionHeader } from '../../../Components';
import { getSiteSectionsFromSiteSectionGroup, getSiteSpacesFromStructure } from '../../../state';
import { persistRecoilEffect } from '../../../utils';
import { SitePublishButton } from './SitePublishButton';

const InvitesDialogContent = React.lazy(() =>
    import('../../InvitesOrganization/InvitesDialogContent').then((m) => ({
        default: m.InvitesDialogContent,
    }))
);

interface Task {
    id: string;
    icon: IconDefinition;
    title: string;
    description: ReactNode;
    actions: (input: {
        organization: api.Organization;
        site: api.Site;
        structure: api.SiteStructure;
        markAsIgnored: () => void;
        logAction: () => void;
    }) => ReactNode;
    isEnabled?: (input: {
        site: api.Site;
        structure: api.SiteStructure;
        customization: api.SiteCustomizationSettings;
    }) => boolean;
    isDone: (input: {
        site: api.Site;
        structure: api.SiteStructure;
        customization: api.SiteCustomizationSettings;
        orgUsageMembers: api.OrganizationUsageMembers | null;
    }) => boolean;
}

export enum SiteGetStartedTaskId {
    InviteTeammates = 'invite-teammates',
    CustomizeLookAndFeel = 'look',
    AddStructure = 'sections',
    AddDomain = 'domain',
    ConfigureAudience = 'va',
    Publish = 'publish',
}

const TASKS: Task[] = [
    {
        id: 'cr',
        icon: Icon.Edit,
        title: 'Edit your content',
        description:
            'Create a change request to edit your site’s content safely, then merge it to update your site when ready.',
        isEnabled: ({ structure }) => {
            const siteSpaces = getSiteSpacesFromStructure(structure);
            return siteSpaces.length > 0;
        },
        isDone: ({ structure }) => {
            const hasSiteSpaceCR = (siteSpace: api.SiteSpace) => siteSpace.space.changeRequests > 0;
            const hasSectionCR = (section: api.SiteSection) =>
                section.siteSpaces.some(hasSiteSpaceCR);
            switch (structure.type) {
                case 'sections':
                    return structure.structure.some((item) => {
                        switch (item.object) {
                            case 'site-section': {
                                return hasSectionCR(item);
                            }
                            case 'site-section-group': {
                                return getSiteSectionsFromSiteSectionGroup(item).some(hasSectionCR);
                            }
                        }
                    });
                case 'siteSpaces':
                    return structure.structure.some(hasSiteSpaceCR);
                default:
                    assertNever(structure);
            }
        },
        actions: ({ logAction, organization, structure, markAsIgnored }) => {
            const siteSpaces = getSiteSpacesFromStructure(structure);
            const firstSiteSpace = siteSpaces[0];
            assert(firstSiteSpace, 'The `isEnabled` method should have been called before');
            return (
                <>
                    <Button
                        label="Open your space to edit"
                        href={{
                            screen: {
                                type: 'space',
                                organization: organization.id,
                                space: firstSiteSpace.space.id,
                            },
                        }}
                        onPress={() => {
                            logAction();
                            toast.info(
                                'Click on "Edit" in the header to start editing your site’s content',
                                {
                                    icon: <Icon.ChangeRequest className="text-merge" />,
                                }
                            );
                        }}
                    />
                    <Button kind="secondary" label="Skip for now" onPress={() => markAsIgnored()} />
                </>
            );
        },
    },
    {
        id: SiteGetStartedTaskId.InviteTeammates,
        icon: Icon.UserAdd,
        title: 'Invite teammates',
        description:
            'GitBook is better with some friendly faces. Invite your teammates to start collaborating straight away.',
        isDone: ({ orgUsageMembers }) => !!orgUsageMembers && orgUsageMembers.total >= 2,
        actions: ({ organization, markAsIgnored, logAction }) => (
            <>
                <DialogRoot>
                    <DialogTrigger>
                        <Button onPress={() => logAction()} label="Invite teammates" />
                    </DialogTrigger>
                    <Dialog>
                        <React.Suspense fallback={<LoadingPanel />}>
                            <InvitesDialogContent organizationId={organization.id} />
                        </React.Suspense>
                    </Dialog>
                </DialogRoot>
                <Button kind="secondary" label="Skip for now" onPress={() => markAsIgnored()} />
            </>
        ),
    },
    {
        id: SiteGetStartedTaskId.CustomizeLookAndFeel,
        icon: Icon.Customization,
        title: 'Customize the look and feel',
        description: 'Add your logo, brand colors, and style to make the site your own.',
        isDone: ({ customization }) => {
            const hasFavicon = 'emoji' in customization.favicon || 'icon' in customization.favicon;
            const hasLogo = Boolean(
                customization.header?.logo?.light || customization.header?.logo?.dark
            );
            return hasFavicon || hasLogo;
        },
        actions: ({ logAction, site, organization, markAsIgnored }) => (
            <>
                <Button
                    label="Customize your site"
                    href={{
                        screen: {
                            type: 'site',
                            organization: organization.id,
                            site: site.id,
                            tab: { type: 'customization', tab: 'general' },
                        },
                    }}
                    onPress={() => logAction()}
                />
                <Button kind="secondary" label="Skip for now" onPress={() => markAsIgnored()} />
            </>
        ),
    },
    {
        id: SiteGetStartedTaskId.AddStructure,
        icon: Icon.SiteStructure,
        title: 'Add structure to your site',
        description:
            'Use site sections to add or organize content and help visitors navigate by topic or product.',
        isDone: ({ structure }) => {
            const hasSections = structure.type === 'sections' && structure.structure.length > 0;
            return hasSections;
        },
        actions: ({ logAction, site, organization, markAsIgnored }) => (
            <>
                <Button
                    label="Manage site structure"
                    href={{
                        screen: {
                            type: 'site',
                            organization: organization.id,
                            site: site.id,
                            tab: { type: 'settings', section: 'structure' },
                        },
                    }}
                    onPress={() => {
                        logAction();
                    }}
                />
                <Button kind="secondary" label="Skip for now" onPress={() => markAsIgnored()} />
            </>
        ),
    },
    {
        id: SiteGetStartedTaskId.AddDomain,
        icon: Icon.Globe,
        title: 'Add a custom domain',
        description: 'Use your own domain to create a seamless, branded experience.',
        isDone: ({ site }) => Boolean(site.hostname || site.proxy),
        actions: ({ logAction, site, organization, markAsIgnored }) => (
            <>
                <Button
                    label="Set up a custom domain"
                    href={{
                        screen: {
                            type: 'site',
                            organization: organization.id,
                            site: site.id,
                            tab: { type: 'settings', section: 'domains' },
                        },
                    }}
                    onPress={() => {
                        logAction();
                    }}
                />
                <Button
                    kind="secondary"
                    label="Use default domain"
                    onPress={() => markAsIgnored()}
                />
            </>
        ),
    },
    {
        id: SiteGetStartedTaskId.ConfigureAudience,
        icon: Icon.PublishStatusVisitorAuth,
        title: 'Control who can view your site',
        description:
            'Choose whether your site is public, or only available to a specific audience.',
        isDone: ({ site }) => site.visibility !== api.SiteVisibility.Public,
        actions: ({ logAction, site, organization, markAsIgnored }) => (
            <>
                <Button
                    label="Configure audience"
                    href={{
                        screen: {
                            type: 'site',
                            organization: organization.id,
                            site: site.id,
                            tab: { type: 'settings', section: 'audience' },
                        },
                    }}
                    onPress={() => {
                        logAction();
                    }}
                />
                <Button kind="secondary" label="Keep it public" onPress={() => markAsIgnored()} />
            </>
        ),
    },
    {
        id: SiteGetStartedTaskId.Publish,
        icon: Icon.Publish,
        title: 'Publish your site',
        description: 'Go live with your docs site and share what you’ve built with your audience.',
        isDone: ({ site }) => {
            return site.published;
        },
        actions: ({ logAction, site, organization }) => (
            <SitePublishButton
                onPress={() => logAction()}
                site={site}
                organization={organization}
            />
        ),
    },
];

const ignoredSiteTaskIdsAtom = atomFamily<Set<string>, string>({
    key: 'ignoredSiteTaskIds',
    default: new Set<string>(),
    effects: [
        persistRecoilEffect({
            serializer: {
                serialize: (value) => JSON.stringify(Array.from(value)),
                deserialize: (value) => new Set(JSON.parse(value)),
            },
        }),
    ],
});

const dismissedSiteGetStartedAtom = atomFamily<boolean, string>({
    key: 'dismissedSiteGetStarted',
    default: false,
    effects: [persistRecoilEffect()],
});

export function useIsSiteGetStartedDismissed(siteId: string) {
    return useRecoilValue(dismissedSiteGetStartedAtom(siteId));
}

export function useSiteGetStartedIgnoredItemIds(siteId: string) {
    return useRecoilValue(ignoredSiteTaskIdsAtom(siteId));
}

export function SiteGetStarted(props: {
    site: api.Site;
    siteCustomization: api.SiteCustomizationSettings;
    siteStructure: api.SiteStructure;
    organization: api.Organization;
    orgUsageMembers: api.OrganizationUsageMembers | null;
}) {
    const { site, siteStructure, siteCustomization, organization, orgUsageMembers } = props;

    const [ignoredItemIds, setIgnoredItemIds] = useRecoilState(ignoredSiteTaskIdsAtom(site.id));
    const [isDismissed, setIsDismissed] = useRecoilState(dismissedSiteGetStartedAtom(site.id));

    const dismiss = useEventCallback((trigger: 'button' | 'automatic') => {
        setIsDismissed(true);
        logEvent(createSiteEvent(site, { type: 'site_get_started_dismiss', trigger }));
    });

    const tasks = useMemo(() => {
        return TASKS.map((item) => {
            if (
                item.isEnabled &&
                !item.isEnabled({
                    site,
                    structure: siteStructure,
                    customization: siteCustomization,
                })
            ) {
                return null;
            }
            return {
                id: item.id,
                icon: item.icon,
                title: item.title,
                description: item.description,
                actions: item.actions({
                    markAsIgnored: () => {
                        setIgnoredItemIds(new Set(ignoredItemIds).add(item.id));
                        logEvent(
                            createSiteEvent(site, {
                                type: 'site_get_started_task_ignore',
                                task: item.id,
                            })
                        );
                    },
                    logAction: () => {
                        logEvent(
                            createSiteEvent(site, {
                                type: 'site_get_started_task_action',
                                task: item.id,
                            })
                        );
                    },
                    site,
                    structure: siteStructure,
                    organization,
                }),
                isDone:
                    ignoredItemIds.has(item.id) ||
                    item.isDone({
                        site,
                        customization: siteCustomization,
                        structure: siteStructure,
                        orgUsageMembers,
                    }),
            };
        }).filter((x) => x !== null);
    }, [
        ignoredItemIds,
        setIgnoredItemIds,
        organization,
        site,
        siteCustomization,
        siteStructure,
        orgUsageMembers,
    ]);

    const pendingTasks = tasks.filter((item) => !item.isDone);
    const firstPendingTask = pendingTasks[0];
    const doneTasks = tasks.filter((item) => item.isDone);

    const completionRatio = doneTasks.length / tasks.length;
    const completionPercent = Math.round(completionRatio * 100);
    const isCompleted = completionPercent === 100;
    const initialIsCompletedRef = useRef(isCompleted);
    useEffect(() => {
        if (initialIsCompletedRef.current) {
            dismiss('automatic');
        }
    }, [dismiss]);

    const getDefalultExpandedKeys = useEventCallback(
        () => new Set(firstPendingTask ? [firstPendingTask.id] : undefined)
    );
    const [expandedKeys, setExpandedKeys] = useState<Set<Key>>(getDefalultExpandedKeys);

    const validExpandedKeys = useMemo(() => {
        return new Set(
            Array.from(expandedKeys).filter((key) => pendingTasks.some((item) => item.id === key))
        );
    }, [expandedKeys, pendingTasks]);

    useEffect(() => {
        if (validExpandedKeys.size === 0 && firstPendingTask) {
            setExpandedKeys(new Set([firstPendingTask.id]));
        }
    }, [validExpandedKeys, firstPendingTask]);

    return (
        <AnimatePresence>
            {isDismissed && site.published ? null : (
                <motion.div
                    className="-mx-4 -mb-4 flex flex-col gap-2 overflow-clip px-4 pb-4"
                    exit={{ height: 0, marginTop: -32, opacity: 0 }}
                >
                    <SiteOverviewSectionHeader>
                        <Heading className="whitespace-nowrap">Get started</Heading>
                        {isCompleted ? (
                            <AtomButton
                                onPress={() => dismiss('button')}
                                className="flex items-center gap-1 text-muted hover:text-base"
                            >
                                Hide
                                <Icon.X className="shrink-0" />
                            </AtomButton>
                        ) : (
                            <div className="flex items-center gap-2">
                                {isCompleted ? (
                                    <div className="flex rounded-full bg-action-success p-0.5">
                                        <Icon.Check className="size-3 text-inverted" />
                                    </div>
                                ) : (
                                    <CircularProgress
                                        value={completionPercent}
                                        className="size-4"
                                    />
                                )}
                                <span className="text-muted">
                                    <span className="tabular-nums">
                                        {doneTasks.length}/{tasks.length}
                                    </span>{' '}
                                    tasks<span className="max-sm:hidden"> completed</span>
                                </span>
                            </div>
                        )}
                    </SiteOverviewSectionHeader>
                    {isCompleted ? (
                        <Card className="flex flex-col items-center gap-4 p-4">
                            <BadgeCompletion />
                            <div className="text-center">
                                <Heading level={3} className="mb-1 text-ui-heading-medium">
                                    All tasks completed!
                                </Heading>
                                <p className="text-muted">
                                    You’re all set! You can now close this checklist or explore more
                                    features.
                                </p>
                                <div className="mt-4 flex flex-wrap justify-center gap-2">
                                    <Button
                                        label="Configure site"
                                        href={{
                                            screen: {
                                                type: 'site',
                                                organization: organization.id,
                                                site: site.id,
                                                tab: { type: 'customization', tab: 'configure' },
                                            },
                                        }}
                                        kind="secondary"
                                    />
                                    <Button
                                        label="Browse integrations"
                                        href={{
                                            screen: {
                                                type: 'integrations',
                                                organization: organization.id,
                                            },
                                        }}
                                        kind="secondary"
                                    />
                                </div>
                            </div>
                        </Card>
                    ) : (
                        <Card className="flex flex-col divide-y">
                            {doneTasks.length > 0 ? (
                                <Disclosure id="done-items">
                                    {({ isExpanded }) => (
                                        <div className="flex w-full flex-col">
                                            <DisclosureTrigger>
                                                <AtomButton className="group flex w-full items-start gap-3 rounded px-4 py-2 text-left text-muted hover:text-base focus-visible:shadow-focus-ring-inset">
                                                    <div className="flex flex-1 items-center gap-0.5">
                                                        {isExpanded ? 'Hide' : 'Show'} completed
                                                        tasks
                                                    </div>
                                                    <DisclosureChevron className="mr-0.5 size-4" />
                                                </AtomButton>
                                            </DisclosureTrigger>
                                            <DisclosurePanel>
                                                {doneTasks.map((item) => (
                                                    <ChecklistItem key={item.id} {...item} />
                                                ))}
                                            </DisclosurePanel>
                                        </div>
                                    )}
                                </Disclosure>
                            ) : null}
                            <DisclosureGroup
                                defaultExpandedKeys={
                                    firstPendingTask ? [firstPendingTask.id] : undefined
                                }
                                expandedKeys={expandedKeys}
                                onExpandedChange={(keys) => {
                                    if (keys.size !== 0) {
                                        setExpandedKeys(keys);
                                    }
                                }}
                                className="flex flex-col divide-y"
                            >
                                {pendingTasks.map((item) => (
                                    <ChecklistItem key={item.id} {...item} />
                                ))}
                            </DisclosureGroup>
                        </Card>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function ChecklistItem(props: {
    id: string;
    icon: IconDefinition;
    title: string;
    description: ReactNode;
    actions: ReactNode;
    isDone: boolean;
}) {
    const { id, icon, title, description, isDone, actions } = props;

    if (isDone) {
        return (
            <div className="flex w-full items-start gap-3 p-4">
                {renderIcon(icon, {
                    className: clsx('mt-px size-4 shrink-0 text-muted'),
                })}
                <div className="flex-1 text-muted text-ui-action line-through">{title}</div>
                <div className="flex rounded-full bg-action-success p-0.5 transition">
                    <Icon.Check className="size-4 text-inverted" />
                </div>
            </div>
        );
    }

    return (
        <Disclosure id={id}>
            {({ isExpanded }) => (
                <div className="flex w-full flex-col p-2">
                    <DisclosureTrigger>
                        <AtomButton
                            className={clsx(
                                'group flex w-full items-start gap-3 rounded px-2 py-2 text-left focus-visible:shadow-focus-ring-inset',
                                isExpanded && 'cursor-default'
                            )}
                        >
                            {renderIcon(icon, {
                                className: 'mt-px size-4 shrink-0',
                            })}
                            <div className="flex-1 text-ui-action">{title}</div>
                            <div
                                className={clsx(
                                    'flex items-center justify-center rounded-full p-0.5 text-muted transition-opacity group-hover:bg-side group-hover:text-base',
                                    isExpanded && 'opacity-0'
                                )}
                            >
                                <Icon.Plus className="size-4" />
                            </div>
                        </AtomButton>
                    </DisclosureTrigger>
                    <DisclosurePanel>
                        <div className="flex flex-1 flex-col gap-3 px-9 pb-2">
                            <p>{description}</p>
                            <div className="flex flex-wrap items-center gap-3">{actions}</div>
                        </div>
                    </DisclosurePanel>
                </div>
            )}
        </Disclosure>
    );
}
